import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ExternalLink, Globe, User, Users, BarChart2,
  Target, Megaphone, ShoppingBag, CheckCircle2, AlertCircle,
  FileText, Edit2, Save, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PageLoader } from '@shared/components/PageLoader';
import { prospectsAPI } from '@/utils/supabase/client';
import type { Prospect, ProspectStatus } from '@/types/prospects';
import { PROSPECT_STATUS_LABELS } from '@/types/prospects';
import { ProspectTierBadge, ProspectStatusBadge } from './ProspectTierBadge';
import { DocumentsPanel } from '@features/documents/components/DocumentsPanel';
import { toast } from 'sonner';
import { cn } from '@/components/ui/utils';

function Section({ title, icon: Icon, children }: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <h3 className="flex items-center gap-2 text-sm font-semibold mb-3">
        <Icon className="h-4 w-4 text-muted-foreground" />
        {title}
      </h3>
      {children}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value || value === 'NULL') return null;
  const isUrl = value.startsWith('http');
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      {isUrl ? (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-info hover:underline flex items-center gap-1"
        >
          <ExternalLink className="h-3 w-3" />
          {value.length > 60 ? value.slice(0, 60) + '…' : value}
        </a>
      ) : (
        <span className="text-sm">{value}</span>
      )}
    </div>
  );
}

function ScoreBar({ label, value, max = 100 }: { label: string; value: number | null; max?: number }) {
  if (value == null) return null;
  const pct = Math.min(100, (value / max) * 100);
  const color = value >= 80 ? 'bg-success' : value >= 65 ? 'bg-info' : 'bg-warning';
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-muted-foreground w-32 shrink-0">{label}</span>
      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
        <div className={cn('h-full rounded-full', color)} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-mono w-6 text-right">{value}</span>
    </div>
  );
}

export function ProspectDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [prospect, setProspect] = useState<Prospect | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    prospectsAPI.getOne(id)
      .then((p) => { setProspect(p); setNotes(p.notes ?? ''); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  const handleSaveNotes = async () => {
    if (!prospect) return;
    setSavingNotes(true);
    try {
      const updated = await prospectsAPI.update(prospect.id, { notes });
      setProspect(updated);
      setEditingNotes(false);
      toast.success('Notes saved');
    } catch {
      toast.error('Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleStatusChange = async (status: ProspectStatus) => {
    if (!prospect) return;
    setUpdatingStatus(true);
    try {
      const updated = await prospectsAPI.update(prospect.id, { status });
      setProspect(updated);
      toast.success(`Status updated to ${PROSPECT_STATUS_LABELS[status]}`);
    } catch {
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (loading) return <PageLoader />;
  if (!prospect) return (
    <div className="flex items-center justify-center h-full text-muted-foreground">
      Prospect not found
    </div>
  );

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Header */}
      <div className="border-b px-4 lg:px-6 py-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/prospects')} className="shrink-0 mt-0.5">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2 flex-wrap">
                <ProspectTierBadge tier={prospect.priority_tier} size="md" />
                <h1 className="text-xl font-semibold font-serif">{prospect.brand}</h1>
                {prospect.website && (
                  <a
                    href={prospect.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <Globe className="h-4 w-4" />
                  </a>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {prospect.category && (
                  <span className="text-sm text-muted-foreground">{prospect.category}</span>
                )}
                {prospect.confidence_level && (
                  <Badge variant="outline" className="text-xs">{prospect.confidence_level} confidence</Badge>
                )}
              </div>
            </div>
          </div>

          {/* Status selector */}
          <div className="flex items-center gap-2 shrink-0">
            <ProspectStatusBadge status={prospect.status} />
            <Select
              value={prospect.status}
              onValueChange={(v) => handleStatusChange(v as ProspectStatus)}
              disabled={updatingStatus}
            >
              <SelectTrigger size="sm" className="w-auto text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(PROSPECT_STATUS_LABELS) as ProspectStatus[]).map((s) => (
                  <SelectItem key={s} value={s} className="text-xs">
                    {PROSPECT_STATUS_LABELS[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-auto p-4 lg:p-6">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-4">

          {/* Left column — 2/3 */}
          <div className="lg:col-span-2 flex flex-col gap-4">

            {/* Why XYZ could win */}
            {prospect.why_xyz_could_win && (
              <Section title="Why XYZ Could Win" icon={Target}>
                <p className="text-sm leading-relaxed">{prospect.why_xyz_could_win}</p>
              </Section>
            )}

            {/* Opportunity analysis */}
            <Section title="Opportunity Analysis" icon={BarChart2}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Lifecycle opportunity" value={prospect.lifecycle_opportunity} />
                <Field label="Landing page / funnel" value={prospect.landing_page_or_funnel_opportunity} />
                <Field label="Retail media" value={prospect.retail_media_opportunity} />
                <Field label="Likely paid media gap" value={prospect.likely_paid_media_gap} />
                <Field label="Paid media maturity" value={prospect.likely_paid_media_maturity} />
              </div>
            </Section>

            {/* Outreach */}
            <Section title="Outreach Strategy" icon={Megaphone}>
              <div className="flex flex-col gap-3">
                <Field label="Why they might say yes" value={prospect.why_this_brand_might_say_yes} />
                <Field label="Suggested outreach angle" value={prospect.suggested_outreach_angle} />
                <Field label="Recommended opener" value={prospect.recommended_opener_path} />
                <Field label="Validation needed" value={prospect.validation_needed} />
              </div>
            </Section>

            {/* Ad research */}
            <Section title="Ad Research" icon={Globe}>
              <div className="flex flex-col gap-3">
                <Field label="Ad library notes" value={prospect.ad_library_notes} />
                <Field label="Creative themes" value={prospect.observed_creative_themes} />
                <Field label="Meta Ad Library" value={prospect.meta_ad_library_url} />
                <Field label="Google Ads Transparency" value={prospect.google_ads_transparency_url} />
              </div>
            </Section>

            {/* Notes */}
            <Section title="Notes" icon={FileText}>
              {editingNotes ? (
                <div className="flex flex-col gap-2">
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={5}
                    placeholder="Add notes, context, or follow-ups…"
                    className="text-sm"
                  />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleSaveNotes} disabled={savingNotes}>
                      <Save className="h-3.5 w-3.5 mr-1" />
                      Save
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => { setEditingNotes(false); setNotes(prospect.notes ?? ''); }}>
                      <X className="h-3.5 w-3.5 mr-1" />
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  className="group flex items-start gap-2 cursor-pointer"
                  onClick={() => setEditingNotes(true)}
                >
                  {prospect.notes ? (
                    <p className="text-sm whitespace-pre-wrap flex-1">{prospect.notes}</p>
                  ) : (
                    <p className="text-sm text-muted-foreground italic flex-1">No notes yet — click to add</p>
                  )}
                  <Edit2 className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 shrink-0 mt-0.5" />
                </div>
              )}
            </Section>
          </div>

          {/* Right column — 1/3 */}
          <div className="flex flex-col gap-4">

            {/* Documents */}
            <Section title="Documents" icon={FileText}>
              <DocumentsPanel prospectId={prospect.id} />
            </Section>

            {/* Scores */}
            <Section title="Scores" icon={BarChart2}>
              <div className="flex flex-col gap-2.5">
                <ScoreBar label="ICP Fit" value={prospect.icp_fit_score} />
                <ScoreBar label="Paid Media Opp." value={prospect.paid_media_opportunity_score} />
                <ScoreBar label="Brand Quality" value={prospect.brand_quality_score} />
                <ScoreBar label="Landability" value={prospect.landability_score} />
                <ScoreBar label="Sean Connection" value={prospect.sean_connection_score} />
                {prospect.final_priority_score != null && (
                  <div className="mt-1 pt-2 border-t flex items-center justify-between">
                    <span className="text-xs font-medium">Final Priority Score</span>
                    <span className="text-sm font-bold">{prospect.final_priority_score.toFixed(1)}</span>
                  </div>
                )}
              </div>
            </Section>

            {/* Network connection */}
            <Section title="Network Connection" icon={Users}>
              <div className="flex flex-col gap-2">
                {prospect.sean_matched_contact && prospect.sean_matched_contact !== 'See Sean path or validate' ? (
                  <>
                    <div>
                      <p className="text-xs text-muted-foreground">Contact</p>
                      <p className="text-sm font-medium">{prospect.sean_matched_contact}</p>
                      {prospect.sean_contact_title && (
                        <p className="text-xs text-muted-foreground">{prospect.sean_contact_title}</p>
                      )}
                      {prospect.sean_contact_company && (
                        <p className="text-xs text-muted-foreground">{prospect.sean_contact_company}</p>
                      )}
                    </div>
                    <Field label="Connection type" value={prospect.sean_connection_type} />
                  </>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    {prospect.sean_connection_type ?? 'No direct connection mapped'}
                  </p>
                )}
              </div>
            </Section>

            {/* Brand info */}
            <Section title="Brand Info" icon={ShoppingBag}>
              <div className="flex flex-col gap-2">
                <Field label="Estimated AOV" value={prospect.estimated_aov} />
                <Field label="Channel model" value={prospect.channel_model} />
                <Field label="Revenue stage" value={prospect.likely_revenue_stage} />
                <Field label="DTC presence" value={prospect.dtc_presence} />
                <Field label="Amazon presence" value={prospect.amazon_presence} />
                <Field label="Target presence" value={prospect.target_presence} />
                {prospect.other_retail_partners && prospect.other_retail_partners !== 'Needs validation' && (
                  <Field label="Other retail" value={prospect.other_retail_partners} />
                )}
              </div>
            </Section>

            {/* Leadership */}
            <Section title="Leadership" icon={User}>
              <div className="flex flex-col gap-2">
                <Field label="CEO / Founder" value={
                  prospect.known_founder_or_ceo !== 'See Sean path or validate' ? prospect.known_founder_or_ceo : null
                } />
                <Field label="Marketing leader" value={
                  prospect.known_marketing_leader !== 'See Sean path or validate' ? prospect.known_marketing_leader : null
                } />
                <Field label="Org type" value={prospect.founder_led_or_corporate} />
              </div>
            </Section>

            {/* Classification */}
            <Section title="Classification" icon={CheckCircle2}>
              <div className="flex flex-col gap-2">
                <div>
                  <p className="text-xs text-muted-foreground">Priority tier</p>
                  <p className="text-sm">{prospect.priority_tier}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Classification</p>
                  <p className="text-sm">{prospect.classification_tier}</p>
                </div>
                {prospect.exclude_reason && (
                  <div className="flex items-start gap-1.5 mt-1">
                    <AlertCircle className="h-3.5 w-3.5 text-warning shrink-0 mt-0.5" />
                    <p className="text-xs text-muted-foreground">{prospect.exclude_reason}</p>
                  </div>
                )}
              </div>
            </Section>
          </div>
        </div>
      </div>
    </div>
  );
}
